// TPLA-209: geborsteld rvs voor de onderdorpel.
// De omgeving en de borstelstructuur worden eenmalig op een canvas gegenereerd (een kleine
// "studio-hdri"), zodat er geen extra bestand gedownload hoeft te worden. Alle onderdorpels
// delen hetzelfde materiaal.

let rvsMaterial = null

// equirectangulaire omgeving: lichte lucht, felle softbox-band op de horizon en een donkere vloer,
// dat geeft de schuine lichtstreep die je op geborsteld rvs ziet
function maakOmgeving (THREE) {
  const canvas = document.createElement('canvas')
  canvas.width = 512
  canvas.height = 256
  const ctx = canvas.getContext('2d')

  const achtergrond = ctx.createLinearGradient(0, 0, 0, canvas.height)
  // middengrijs met een rustig verloop: genoeg verschil voor glans, maar nergens wit of zwart
  achtergrond.addColorStop(0, '#b8b8b8')
  achtergrond.addColorStop(0.35, '#999999')
  achtergrond.addColorStop(0.47, '#8a8a8a')
  achtergrond.addColorStop(0.53, '#808080')
  achtergrond.addColorStop(0.7, '#707070')
  achtergrond.addColorStop(1, '#646464')
  ctx.fillStyle = achtergrond
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // softboxen: zachte, felle lichtvlakken die als lichtstrepen in het metaal terugkomen
  const softbox = (x, y, breedte, hoogte) => {
    const licht = ctx.createLinearGradient(0, y - hoogte / 2, 0, y + hoogte / 2)
    licht.addColorStop(0, 'rgba(255,255,255,0)')
    licht.addColorStop(0.5, 'rgba(255,255,255,0.55)')
    licht.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = licht
    ctx.fillRect(x, y - hoogte / 2, breedte, hoogte)
  }
  // zachte lichtband rond de horizon: die ziet de verticale voorkant van de dorpel
  softbox(0, 118, canvas.width, 30)
  softbox(40, 70, 160, 28)
  softbox(300, 80, 140, 24)

  const texture = new THREE.CanvasTexture(canvas)
  texture.mapping = THREE.EquirectangularReflectionMapping
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
}

// fijne lengtestrepen in de ruwheid, zodat de reflectie "geborsteld" uitgesmeerd wordt
function maakBorstelStructuur (THREE) {
  const canvas = document.createElement('canvas')
  canvas.width = 1024
  canvas.height = 128
  const ctx = canvas.getContext('2d')

  ctx.fillStyle = '#808080'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  for (let i = 0; i < 1400; i++) {
    const grijs = Math.floor(90 + Math.random() * 90)
    ctx.strokeStyle = `rgba(${grijs},${grijs},${grijs},${0.25 + Math.random() * 0.4})`
    ctx.lineWidth = Math.random() < 0.85 ? 1 : 2
    const y = Math.random() * canvas.height
    const x = Math.random() * canvas.width
    const lengte = 150 + Math.random() * 600
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x + lengte, y)
    ctx.stroke()
  }

  const texture = new THREE.CanvasTexture(canvas)
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  texture.repeat.set(2, 1)
  texture.anisotropy = 16
  return texture
}

// kleurtexture van geborsteld rvs: lichtgrijs verloop, een schuine zachte lichtstreep en fijne
// borstelstrepen in de lengte. Zo ziet de dorpel er ook zonder sterke reflecties uit als rvs.
function maakRvsTexture (THREE) {
  const canvas = document.createElement('canvas')
  canvas.width = 1024
  canvas.height = 256
  const ctx = canvas.getContext('2d')

  // basis: van iets donkerder grijs naar lichter grijs
  const basis = ctx.createLinearGradient(0, 0, canvas.width, canvas.height)
  basis.addColorStop(0, '#9c9fa2')
  basis.addColorStop(0.5, '#c4c7ca')
  basis.addColorStop(1, '#8f9295')
  ctx.fillStyle = basis
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // schuine zachte lichtstreep
  ctx.save()
  ctx.translate(canvas.width * 0.45, canvas.height / 2)
  ctx.rotate(-0.12)
  const streep = ctx.createLinearGradient(0, -90, 0, 90)
  streep.addColorStop(0, 'rgba(255,255,255,0)')
  streep.addColorStop(0.5, 'rgba(255,255,255,0.45)')
  streep.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = streep
  ctx.fillRect(-canvas.width, -90, canvas.width * 2, 180)
  ctx.restore()

  // borstelstrepen: veel dunne lichte en donkere lijnen in de lengte
  for (let i = 0; i < 2600; i++) {
    const licht = Math.random() < 0.5
    const alpha = 0.04 + Math.random() * 0.1
    ctx.strokeStyle = licht ? `rgba(255,255,255,${alpha})` : `rgba(40,42,45,${alpha})`
    ctx.lineWidth = Math.random() < 0.9 ? 1 : 2
    const y = Math.random() * canvas.height
    const x = Math.random() * canvas.width - 200
    const lengte = 200 + Math.random() * 800
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x + lengte, y)
    ctx.stroke()
  }

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  texture.anisotropy = 16
  return texture
}

export function getRvsMaterial (THREE) {
  if (rvsMaterial) return rvsMaterial

  const borstel = maakBorstelStructuur(THREE)

  rvsMaterial = new THREE.MeshPhysicalMaterial({
    // de rvs-look komt vooral uit de texture; de metaalwaarde staat lager zodat die texture zichtbaar blijft
    color: new THREE.Color('#ffffff'),
    map: maakRvsTexture(THREE),
    metalness: 0.6,
    roughness: 0.4,
    roughnessMap: borstel,
    // een dunne, gladde clearcoat erover geeft de glans
    clearcoat: 0.6,
    clearcoatRoughness: 0.12,
    envMap: maakOmgeving(THREE),
    envMapIntensity: 1
  })

  return rvsMaterial
}
